import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconAlertTriangle, IconRefresh, IconSettings } from "@tabler/icons-react"
import { useEffect, useMemo, useState } from "react"
import { CardGridSkeleton } from "../../../../components/PageSkeletons"
import { formatAmount, formatChecked, formatDays } from "./format"
import type { IProps, IProviderCredit, IProviderCreditEditValues, TCreditStatus, TProviderCategory } from "./interfaces"
import classes from "./styles.module.css"

const CATEGORY_ORDER: TProviderCategory[] = ["llm", "transcription", "image"]

function CategoryTitle({ category }: { category: TProviderCategory }): React.JSX.Element {
  if (category === "llm") return <Trans>AI / LLM</Trans>
  if (category === "transcription") return <Trans>Transcription</Trans>
  return <Trans>Image generation</Trans>
}

function StatusBadge({ status }: { status: TCreditStatus }): React.JSX.Element {
  switch (status) {
    case "ok":
      return (
        <Badge color="success" variant="light">
          <Trans>OK</Trans>
        </Badge>
      )
    case "low":
      return (
        <Badge color="warning" variant="light">
          <Trans>Low</Trans>
        </Badge>
      )
    case "critical":
      return (
        <Badge color="danger" variant="filled">
          <Trans>Critical</Trans>
        </Badge>
      )
    case "unreachable":
      return (
        <Badge color="warning" variant="outline">
          <Trans>Unreachable</Trans>
        </Badge>
      )
    case "unconfigured":
      return (
        <Badge color="gray" variant="light">
          <Trans>Not configured</Trans>
        </Badge>
      )
    default:
      return (
        <Badge color="gray" variant="light">
          <Trans>Unknown</Trans>
        </Badge>
      )
  }
}

function SourceHint({ provider }: { provider: IProviderCredit }): React.JSX.Element {
  if (provider.balanceSource === "live-api") return <Trans>Live balance</Trans>
  if (provider.balanceSource === "manual-topup") return <Trans>Manual top-up − spend</Trans>
  return <Trans>No credit data</Trans>
}

function InfoRow({ label, value }: { label: React.ReactNode; value: React.ReactNode }): React.JSX.Element {
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xs" fw={500} ta="right">
        {value}
      </Text>
    </Group>
  )
}

interface ICardProps {
  provider: IProviderCredit
  onConfigure: (provider: IProviderCredit) => void
}

function ProviderCard({ provider, onConfigure }: ICardProps): React.JSX.Element {
  const { i18n } = useLingui()
  const days = formatDays(provider.daysRemaining)
  return (
    <Card withBorder radius="md" padding="md" className={classes.card} data-status={provider.status}>
      <Group justify="space-between" mb={4} wrap="nowrap">
        <Text fw={600}>{provider.displayName}</Text>
        <StatusBadge status={provider.status} />
      </Group>

      <Text className={classes.remaining} fw={700}>
        {formatAmount(provider.remaining, provider.unit)}
      </Text>
      <Text size="xs" c="dimmed" mb="sm">
        <SourceHint provider={provider} />
      </Text>

      <Stack gap={4}>
        <InfoRow label={<Trans>Spent this month</Trans>} value={formatAmount(provider.monthlySpend, "usd")} />
        {days ? <InfoRow label={<Trans>Runway</Trans>} value={days} /> : null}
        <InfoRow
          label={<Trans>Low / critical</Trans>}
          value={`${formatAmount(provider.lowThreshold, provider.unit)} / ${formatAmount(
            provider.criticalThreshold,
            provider.unit,
          )}`}
        />
      </Stack>

      {provider.error ? (
        <Text size="xs" c="danger" mt="xs" lineClamp={2}>
          {provider.error}
        </Text>
      ) : null}

      <Group justify="space-between" mt="sm" wrap="nowrap">
        <Text size="xs" c="dimmed">
          <Trans>Checked</Trans> {formatChecked(i18n, provider.lastCheckedAt)}
        </Text>
        <Button
          size="compact-sm"
          variant="subtle"
          leftSection={<IconSettings size={14} />}
          onClick={() => onConfigure(provider)}
        >
          <Trans>Configure</Trans>
        </Button>
      </Group>
    </Card>
  )
}

interface IModalProps {
  provider: IProviderCredit
  isSaving: boolean
  onSave: (provider: string, values: IProviderCreditEditValues) => void
  onClose: () => void
}

function ConfigureModal({ provider, isSaving, onSave, onClose }: IModalProps): React.JSX.Element {
  const isUsd = provider.unit === "usd"
  const form = useForm<{
    manualBalance: number | string
    lowThreshold: number | string
    criticalThreshold: number | string
    emailAlertsEnabled: boolean
    alertEmail: string
  }>({
    mode: "uncontrolled",
    initialValues: {
      manualBalance: "",
      lowThreshold: provider.lowThreshold ?? "",
      criticalThreshold: provider.criticalThreshold ?? "",
      emailAlertsEnabled: provider.emailAlertsEnabled,
      alertEmail: provider.alertEmail ?? "",
    },
  })

  const handleSubmit = (values: typeof form.values): void => {
    const out: IProviderCreditEditValues = { unit: provider.unit }
    if (typeof values.manualBalance === "number") out.manualBalance = values.manualBalance
    if (form.isDirty("lowThreshold")) {
      out.lowThreshold = values.lowThreshold === "" ? null : Number(values.lowThreshold)
    }
    if (form.isDirty("criticalThreshold")) {
      out.criticalThreshold = values.criticalThreshold === "" ? null : Number(values.criticalThreshold)
    }
    if (form.isDirty("emailAlertsEnabled")) out.emailAlertsEnabled = values.emailAlertsEnabled
    if (form.isDirty("alertEmail")) out.alertEmail = values.alertEmail.trim() === "" ? null : values.alertEmail.trim()
    onSave(provider.provider, out)
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={`${provider.displayName} — ${provider.unit === "usd" ? "USD" : "credits"}`}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <div>
            <NumberInput
              label={<Trans>Record a top-up (new current balance)</Trans>}
              placeholder={isUsd ? "0.00" : "0"}
              prefix={isUsd ? "$" : undefined}
              suffix={isUsd ? undefined : " credits"}
              min={0}
              decimalScale={isUsd ? 2 : 0}
              key={form.key("manualBalance")}
              {...form.getInputProps("manualBalance")}
            />
            <Text size="xs" c="dimmed" mt={4}>
              <Trans>Current:</Trans> {formatAmount(provider.manualBalance, provider.unit)} ·{" "}
              <Trans>setting this resets the spend counter to now</Trans>
            </Text>
          </div>

          <Group grow>
            <NumberInput
              label={<Trans>Low threshold</Trans>}
              placeholder="auto (14d)"
              prefix={isUsd ? "$" : undefined}
              min={0}
              decimalScale={isUsd ? 2 : 0}
              key={form.key("lowThreshold")}
              {...form.getInputProps("lowThreshold")}
            />
            <NumberInput
              label={<Trans>Critical threshold</Trans>}
              placeholder="auto (5d)"
              prefix={isUsd ? "$" : undefined}
              min={0}
              decimalScale={isUsd ? 2 : 0}
              key={form.key("criticalThreshold")}
              {...form.getInputProps("criticalThreshold")}
            />
          </Group>
          <Text size="xs" c="dimmed">
            <Trans>Leave a threshold blank to auto-alert at ~14 days (low) / ~5 days (critical) of burn.</Trans>
          </Text>

          <Switch
            label={<Trans>Email alerts when low</Trans>}
            key={form.key("emailAlertsEnabled")}
            {...form.getInputProps("emailAlertsEnabled", { type: "checkbox" })}
          />
          <TextInput
            label={<Trans>Alert email (optional)</Trans>}
            placeholder="ops@lexicanon.com"
            type="email"
            key={form.key("alertEmail")}
            {...form.getInputProps("alertEmail")}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" loading={isSaving}>
              <Trans>Save</Trans>
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

function groupByCategory(providers: IProviderCredit[]): Record<TProviderCategory, IProviderCredit[]> {
  const out: Record<TProviderCategory, IProviderCredit[]> = { llm: [], transcription: [], image: [] }
  for (const p of providers) out[p.category].push(p)
  return out
}

export function ProviderCreditsView({
  providers,
  isLoading,
  isRefreshing,
  isSaving,
  onRefresh,
  onSave,
}: IProps): React.JSX.Element {
  const [editing, setEditing] = useState<IProviderCredit | null>(null)
  const [pendingClose, setPendingClose] = useState(false)

  const grouped = useMemo(() => groupByCategory(providers), [providers])
  const attention = useMemo(
    () => providers.filter((p) => p.status === "critical" || p.status === "low" || p.status === "unreachable"),
    [providers],
  )

  // Close the modal once a save round-trip finishes (isSaving true → false).
  useEffect(() => {
    if (pendingClose && !isSaving) {
      setEditing(null)
      setPendingClose(false)
    }
  }, [pendingClose, isSaving])

  const handleSave = (provider: string, values: IProviderCreditEditValues): void => {
    onSave(provider, values)
    setPendingClose(true)
  }

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="md" wrap="nowrap">
        <div>
          <Title order={2}>
            <Trans>Provider credits</Trans>
          </Title>
          <Text c="dimmed" size="sm">
            <Trans>Status and remaining credit for every third-party API.</Trans>
          </Text>
        </div>
        <Button onClick={onRefresh} loading={isRefreshing} leftSection={<IconRefresh size={16} />} variant="default">
          <Trans>Refresh</Trans>
        </Button>
      </Group>

      {attention.length > 0 ? (
        <Alert color="warning" icon={<IconAlertTriangle size={16} />} mb="md">
          <Plural
            value={attention.length}
            one="# provider needs attention — top it up before it runs out."
            other="# providers need attention — top them up before they run out."
          />
        </Alert>
      ) : null}

      {isLoading ? (
        <CardGridSkeleton />
      ) : (
        <Stack gap="xl">
          {CATEGORY_ORDER.map((cat) =>
            grouped[cat].length > 0 ? (
              <div key={cat}>
                <Title order={4} mb="sm">
                  <CategoryTitle category={cat} />
                </Title>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                  {grouped[cat].map((p) => (
                    <ProviderCard key={p.provider} provider={p} onConfigure={setEditing} />
                  ))}
                </SimpleGrid>
              </div>
            ) : null,
          )}
        </Stack>
      )}

      {editing ? (
        <ConfigureModal
          key={editing.provider}
          provider={editing}
          isSaving={isSaving}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Container>
  )
}
